import { useEffect, useMemo, useState, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { disableAnalytics, identifyAnalyticsUser, loadAnalytics } from '@/lib/analytics';
import { USER_STORAGE_KEY } from '@/lib/authStorage';
import styles from './CookieConsentManager.module.css';
import {
  COOKIE_CONSENT_UPDATED_EVENT,
  COOKIE_PREFERENCES_OPEN_EVENT,
  CookieConsentPreferences,
  CookieConsentChoice,
  readCookieConsent,
  saveCookieConsent,
} from '@/lib/cookieConsent';

// Consent controls also appear on the lightweight landing page. Native buttons
// preserve their behavior without loading the dashboard's MUI theme and engine.
const Button = ({ className, variant, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'outline';
}) => <button
  {...props}
  className={cn(
    'inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 font-inter text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    variant === 'outline' ? 'border bg-transparent' : 'border border-transparent',
    className,
  )}
/>;

const getDefaultDraft = (consent: CookieConsentPreferences | null) => ({
  analytics: consent?.analytics ?? false,
  marketing: consent?.marketing ?? false,
});

const CookieToggleRow = ({
  checked,
  description,
  disabled,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  description: string;
  disabled?: boolean;
  label: string;
  onCheckedChange?: (checked: boolean) => void;
}) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900">{label}</p>
      <p className="mt-1 text-sm leading-5 text-slate-600">{description}</p>
    </div>
    <Switch
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      className="mt-1 data-[state=checked]:bg-[#20AEDC]"
    />
  </div>
);

const CookieConsentManager = () => {
  const [consent, setConsent] = useState<CookieConsentPreferences | null>(() => readCookieConsent());
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [draft, setDraft] = useState(() => getDefaultDraft(consent));
  const shouldShowBanner = !consent && !isPreferencesOpen;

  const preferenceSummary = useMemo(
    () => `Analytics cookies are ${draft.analytics ? 'allowed' : 'blocked'}. Advertising cookies are ${draft.marketing ? 'allowed' : 'blocked'}.`,
    [draft.analytics, draft.marketing]
  );

  useEffect(() => {
    if (consent?.analytics || consent?.marketing) {
      if (consent.analytics) {
        // Refresh returning profiles without loading API clients on the landing page.
        try {
          const stored: unknown = JSON.parse(window.localStorage.getItem(USER_STORAGE_KEY) || 'null');
          if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
            const user = stored as Record<string, unknown>;
            if (typeof user.id === 'string' && typeof user.token === 'string' && user.token) {
              identifyAnalyticsUser(user);
            }
          }
        } catch {
          // Unavailable or invalid storage must not prevent consent from being applied.
        }
      }
      loadAnalytics();
      return;
    }

    disableAnalytics();
  }, [consent]);

  useEffect(() => {
    const handleOpenPreferences = () => {
      const storedConsent = readCookieConsent();
      setConsent(storedConsent);
      setDraft(getDefaultDraft(storedConsent));
      setIsPreferencesOpen(true);
    };

    const handleConsentUpdated = (event: Event) => {
      const detail = (event as CustomEvent<CookieConsentPreferences>).detail;
      setConsent(detail);
      setDraft(getDefaultDraft(detail));
    };

    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);

    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleOpenPreferences);
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, []);

  const commitConsent = (choice: CookieConsentChoice) => {
    const nextConsent = saveCookieConsent(choice);
    setConsent(nextConsent);
    setDraft(getDefaultDraft(nextConsent));
    setIsPreferencesOpen(false);
  };

  const openCustomize = () => {
    setDraft(getDefaultDraft(consent));
    setIsPreferencesOpen(true);
  };

  return (
    <>
      {shouldShowBanner && (
        <section
          aria-label="Cookie consent"
          className={cn(styles.banner, 'border-t border-slate-200 bg-white shadow-[0_-1rem_2.5rem_rgba(15,23,42,0.14)]')}
        >
          <div className={styles.bannerInner}>
            <div className="max-w-3xl">
              <h2 className="hidden text-base font-semibold text-slate-950 sm:block">Cookie Preferences</h2>
              <p className="text-xs leading-[18px] text-slate-600 sm:hidden">
                Essential cookies keep the site working. Choose optional analytics and ads cookies.
              </p>
              <p className="mt-1 hidden text-sm leading-6 text-slate-600 sm:block">
                We use necessary cookies to keep CalorieScience working. With your permission,
                analytics cookies help us improve the product and advertising cookies help measure
                campaigns. You can choose each category separately.
              </p>
            </div>
            <div className={styles.bannerActions}>
              <Button
                type="button"
                variant="outline"
                className="h-11 min-w-0 border-slate-300 px-2 text-xs text-slate-700 hover:bg-slate-50 sm:px-4 sm:text-sm"
                onClick={() => commitConsent({ analytics: false, marketing: false })}
              >
                Reject all
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 min-w-0 border-[#20AEDC] px-2 text-xs text-[#0F7FA4] hover:bg-[#EEF9FD] sm:px-4 sm:text-sm"
                onClick={openCustomize}
              >
                Customize
              </Button>
              <Button
                type="button"
                className="h-11 min-w-0 bg-[#20AEDC] px-2 text-xs text-white hover:bg-[#178fb7] sm:px-4 sm:text-sm"
                onClick={() => commitConsent({ analytics: true, marketing: true })}
              >
                Accept all
              </Button>
            </div>
          </div>
        </section>
      )}

      <Dialog open={isPreferencesOpen} onOpenChange={setIsPreferencesOpen}>
        <DialogContent className="max-h-[min(88vh,42rem)] max-w-[36rem] overflow-y-auto rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-950">Cookie Preferences</DialogTitle>
            <DialogDescription className="text-sm leading-6 text-slate-600">
              Choose which optional cookies CalorieScience can use. You can update this anytime
              from Cookie Preferences.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <CookieToggleRow
              checked
              disabled
              label="Strictly necessary cookies"
              description="Required for security, authentication, preferences, and core site features."
            />
            <CookieToggleRow
              checked={draft.analytics}
              label="Analytics cookies"
              description="Allows Google Analytics, Microsoft Clarity, and Mixpanel to help us understand website and product usage."
              onCheckedChange={(analytics) => setDraft((current) => ({ ...current, analytics }))}
            />
            <CookieToggleRow
              checked={draft.marketing}
              label="Advertising cookies"
              description="Allows Google Ads, Meta Pixel, and LinkedIn Insight to measure campaigns and personalise ads. When you sign up, we may share a hashed email with Google to match your signup to an ad."
              onCheckedChange={(marketing) => setDraft((current) => ({ ...current, marketing }))}
            />
          </div>

          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{preferenceSummary}</p>

          <DialogFooter className="gap-2 sm:flex-wrap sm:justify-between sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              onClick={() => commitConsent({ analytics: false, marketing: false })}
            >
              Reject all
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                className="border-[#20AEDC] text-[#0F7FA4] hover:bg-[#EEF9FD]"
                onClick={() => commitConsent(draft)}
              >
                Save preferences
              </Button>
              <Button
                type="button"
                className="bg-[#20AEDC] text-white hover:bg-[#178fb7]"
                onClick={() => commitConsent({ analytics: true, marketing: true })}
              >
                Accept all
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentManager;
